import { useState, useEffect, useCallback } from "react";
import { getSupabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import DealerProductsTab from "./components/DealerProductsTab";
import DealerProductAddModal from "./components/DealerProductAddModal";

interface CurrentDealer {
  id: string;
  name: string;
  phone: string;
  address: string;
  balance: number;
  share_token: string;
  dealer_id: string;
}

interface SaleItemDetail {
  id: string;
  sale_id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  purchase_price: number;
  description: string | null;
}

interface SaleDetail {
  id: string;
  total_amount: number;
  payment_method: string;
  created_at: string;
  sale_items: SaleItemDetail[];
}

interface PaymentDetail {
  id: string;
  amount: number;
  status: string;
  note: string;
  receipt_url: string;
  created_at: string;
}

interface ProductInfo {
  id: string;
  name: string;
  brand: string;
  product_code: string;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const EXCEL_FILE_URL = "https://storage.readdy-site.link/project_files/74b4b1d7-f5ca-49e5-aa66-f0217ca9b7e9/1ec9b094-d1ac-4912-8266-bb261b274ca5_CAR-RAPORU-2.xlsx";

export default function DealersPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId || user?.id;

  const [dealers, setDealers] = useState<CurrentDealer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<CurrentDealer | null>(null);
  const [form, setForm] = useState({ name: "", phone: "", address: "", balance: 0 });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDealer, setPaymentDealer] = useState<CurrentDealer | null>(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [detailDealer, setDetailDealer] = useState<CurrentDealer | null>(null);
  const [salesDetail, setSalesDetail] = useState<SaleDetail[]>([]);
  const [paymentsDetail, setPaymentsDetail] = useState<PaymentDetail[]>([]);
  const [productsMap, setProductsMap] = useState<Record<string, ProductInfo>>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [showProductAdd, setShowProductAdd] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: boolean; inserted: number; message: string } | null>(null);

  const fetchDealers = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from("current_dealers")
        .select("id, name, phone, address, balance, share_token, dealer_id")
        .eq("dealer_id", dealerId)
        .order("created_at", { ascending: false });

      if (err) throw err;
      setDealers(data || []);
    } catch (err: any) {
      setError("Bayiler yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  const handleExcelImport = async () => {
    if (!dealerId) return;
    if (!confirm("Excel'deki tüm cari bayileri içe aktarmak istediğinize emin misiniz? Mevcut bayiler değiştirilmeyecek, yeni kayıtlar eklenecek.")) return;
    setImporting(true);
    setImportResult(null);
    try {
      const supabase = getSupabase();
      const { data, error: fnError } = await supabase.functions.invoke("import-excel-dealers", {
        body: { fileUrl: EXCEL_FILE_URL, dealerId },
      });

      if (fnError) {
        throw new Error(fnError.message || "İçe aktarma başarısız");
      }
      if (!data || data.error) {
        throw new Error(data?.error || "İçe aktarma başarısız");
      }

      setImportResult({ success: true, inserted: data.inserted, message: `${data.inserted} cari bayi başarıyla eklendi.` });
      fetchDealers();
    } catch (err: any) {
      setImportResult({ success: false, inserted: 0, message: err.message || "İçe aktarma sırasında hata oluştu." });
    } finally {
      setImporting(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "", phone: "", address: "", balance: 0 });
    setShowModal(true);
  };

  const openEdit = (d: CurrentDealer) => {
    setEditing(d);
    setForm({ name: d.name, phone: d.phone, address: d.address, balance: Number(d.balance) });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim() || !dealerId) return;
    try {
      const supabase = getSupabase();
      if (editing) {
        const { error: err } = await supabase
          .from("current_dealers")
          .update({ name: form.name, phone: form.phone, address: form.address, balance: form.balance })
          .eq("id", editing.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase
          .from("current_dealers")
          .insert({ dealer_id: dealerId, name: form.name, phone: form.phone, address: form.address, balance: form.balance });
        if (err) throw err;
      }
      setShowModal(false);
      fetchDealers();
    } catch (err: any) {
      setError(err.message || "Kaydedilirken hata oluştu.");
    }
  };

  const deleteDealer = async (id: string) => {
    if (!confirm("Bu cari bayiyi silmek istediğinize emin misiniz?")) return;
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase.from("current_dealers").delete().eq("id", id);
      if (err) throw err;
      fetchDealers();
    } catch (err: any) {
      setError(err.message || "Silinirken hata oluştu.");
    }
  };

  const openPayment = (d: CurrentDealer) => {
    setPaymentDealer(d);
    setPaymentAmount(0);
    setPaymentMethod(null);
    setPaymentSuccess(false);
    setShowPaymentModal(true);
  };

  const confirmPayment = async () => {
    if (!paymentDealer || !paymentMethod || paymentAmount <= 0 || !dealerId) return;
    setSubmitting(true);
    try {
      const supabase = getSupabase();
      const newBalance = Number(paymentDealer.balance) - paymentAmount;

      // Update current dealer balance
      const { error: balErr } = await supabase
        .from("current_dealers")
        .update({ balance: newBalance })
        .eq("id", paymentDealer.id);

      if (balErr) throw balErr;

      // Record as payment notification (completed for direct admin entry)
      const { error: payErr } = await supabase.from("payment_notifications").insert({
        dealer_id: dealerId,
        current_dealer_id: paymentDealer.id,
        amount: paymentAmount,
        status: "completed",
        note: `Admin tarafından ${paymentMethod === "nakit" ? "nakit" : paymentMethod === "pos" ? "POS" : "IBAN"} ile alındı.`,
      });

      if (payErr) throw payErr;

      // Record as sale if needed? Actually this is just a payment receipt, not a sale.
      // The user's requirement: payments reduce balance, which is already done above.

      setPaymentSuccess(true);
      fetchDealers();
    } catch (err: any) {
      setError(err.message || "Ödeme kaydedilirken hata oluştu.");
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (dealer: CurrentDealer) => {
    setDetailDealer(dealer);
    setDetailLoading(true);
    setSalesDetail([]);
    setPaymentsDetail([]);
    try {
      const supabase = getSupabase();

      // Fetch sales for this current_dealer
      const { data: salesData } = await supabase
        .from("sales")
        .select("id, total_amount, payment_method, created_at, sale_items(id, quantity, unit_price, purchase_price, product_id, description)")
        .eq("current_dealer_id", dealer.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (salesData) {
        setSalesDetail(salesData as unknown as SaleDetail[]);

        // Collect all product IDs
        const productIds = new Set<string>();
        (salesData as unknown as SaleDetail[]).forEach((s) => {
          (s.sale_items || []).forEach((si) => {
            if (si.product_id) productIds.add(si.product_id);
          });
        });

        // Fetch product info for all product IDs
        if (productIds.size > 0) {
          const { data: prodData } = await supabase
            .from("products")
            .select("id, name, brand, product_code")
            .in("id", [...productIds]);

          if (prodData) {
            const map: Record<string, ProductInfo> = {};
            (prodData as ProductInfo[]).forEach((p) => { map[p.id] = p; });
            setProductsMap(map);
          }
        }
      }

      // Fetch payment notifications
      const { data: payData } = await supabase
        .from("payment_notifications")
        .select("id, amount, status, note, receipt_url, created_at")
        .eq("current_dealer_id", dealer.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (payData) {
        setPaymentsDetail(payData as PaymentDetail[]);
      }
    } catch (_err) {
      // silently fail, detail is non-critical
    } finally {
      setDetailLoading(false);
    }
  };

  const paymentMethodLabel = (method: string) => {
    if (method === "nakit") return { label: "Nakit", icon: "ri-money-dollar-circle-line", color: "text-emerald-600 bg-emerald-50" };
    if (method === "pos") return { label: "POS", icon: "ri-bank-card-line", color: "text-amber-600 bg-amber-50" };
    if (method === "iban") return { label: "IBAN", icon: "ri-bank-line", color: "text-sky-600 bg-sky-50" };
    if (method === "cari") return { label: "Cari", icon: "ri-exchange-dollar-line", color: "text-accent-600 bg-accent-100" };
    return { label: method, icon: "ri-question-line", color: "text-foreground-500 bg-background-100" };
  };

  const paymentStatusLabel = (status: string) => {
    if (status === "completed" || status === "Onaylandı") return { label: "Tamamlandı", color: "bg-emerald-100 text-emerald-700" };
    if (status === "pending" || status === "Beklemede") return { label: "Beklemede", color: "bg-amber-100 text-amber-700" };
    if (status === "rejected" || status === "Reddedildi") return { label: "Reddedildi", color: "bg-accent-100 text-accent-700" };
    return { label: status, color: "bg-background-100 text-foreground-600" };
  };

  const totalSalesAmount = salesDetail.reduce((sum, s) => sum + Number(s.total_amount), 0);
  const totalPaymentAmount = paymentsDetail.reduce((sum, p) => sum + Number(p.amount), 0);

  const copyShareLink = (dealer: CurrentDealer) => {
    const base = window.location.origin;
    const url = `${base}/statement/${dealer.share_token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(dealer.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const totalBalance = dealers.reduce((sum, d) => sum + Number(d.balance), 0);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="dealers-title">Cari Bayiler</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Toplam bakiye: <strong className="text-foreground-900">${totalBalance.toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong></p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExcelImport}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {importing ? (
              <><i className="ri-loader-4-line animate-spin"></i> Aktarılıyor...</>
            ) : (
              <><i className="ri-file-excel-line"></i> Excel'den İçe Aktar</>
            )}
          </button>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i> Cari Bayi Ekle
          </button>
        </div>
      </div>

      {importResult && (
        <div className={`rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2 ${importResult.success ? "bg-emerald-100 border border-emerald-300 text-emerald-900" : "bg-accent-100 border border-accent-300 text-accent-900"}`}>
          <i className={importResult.success ? "ri-check-line" : "ri-error-warning-line"}></i>
          {importResult.message}
          <button onClick={() => setImportResult(null)} className={`ml-auto font-medium cursor-pointer ${importResult.success ? "text-emerald-700" : "text-accent-700"}`}>Kapat</button>
        </div>
      )}

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={() => setError("")} className="ml-auto text-accent-700 font-medium cursor-pointer">Kapat</button>
        </div>
      )}

      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {/* Desktop Table */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm min-w-[650px]">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Bayi Adı</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Telefon</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Adres</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Bakiye</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-foreground-400">
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    <p className="mt-2">Yükleniyor...</p>
                  </td>
                </tr>
              ) : dealers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-foreground-400">
                    <i className="ri-user-star-line text-2xl block mb-2"></i>
                    Henüz cari bayi bulunmuyor.
                  </td>
                </tr>
              ) : (
                dealers.map((d) => (
                  <tr key={d.id} onClick={() => openDetail(d)} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors cursor-pointer">
                    <td className="px-4 py-3 font-medium text-foreground-900">{d.name}</td>
                    <td className="px-4 py-3 text-foreground-600">{d.phone}</td>
                    <td className="px-4 py-3 text-foreground-600 text-xs">{d.address}</td>
                    <td className={`px-4 py-3 text-right font-medium ${Number(d.balance) >= 0 ? "text-primary-600" : "text-accent-500"}`}>
                      ${Number(d.balance).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openDetail(d)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer" title="Detay">
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                        <button onClick={() => copyShareLink(d)} className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors cursor-pointer ${copiedId === d.id ? "bg-primary-100 text-primary-600" : "text-secondary-500 hover:text-secondary-600 hover:bg-secondary-100"}`} title="Link Paylaş">
                          <i className={`${copiedId === d.id ? "ri-check-line" : "ri-link"} text-sm`}></i>
                        </button>
                        <button onClick={() => openPayment(d)} className="w-8 h-8 rounded-md flex items-center justify-center text-accent-500 hover:text-accent-600 hover:bg-accent-100 cursor-pointer" title="Ödeme Al">
                          <i className="ri-money-dollar-circle-line text-sm"></i>
                        </button>
                        <button onClick={() => openEdit(d)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                        <button onClick={() => deleteDealer(d.id)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-background-200/70">
          {loading ? (
            <div className="px-4 py-8 text-center text-sm text-foreground-400">
              <i className="ri-loader-4-line animate-spin text-xl"></i>
              <p className="mt-2">Yükleniyor...</p>
            </div>
          ) : dealers.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-foreground-400">
              <i className="ri-user-star-line text-2xl block mb-2"></i>
              Henüz cari bayi bulunmuyor.
            </div>
          ) : (
            dealers.map((d) => (
              <div key={d.id} onClick={() => openDetail(d)} className="px-4 py-3 cursor-pointer">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900">{d.name}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {d.phone && <span className="text-xs text-foreground-500 flex items-center gap-1"><i className="ri-phone-line text-xs"></i>{d.phone}</span>}
                      {d.address && <span className="text-xs text-foreground-400 truncate block">{d.address}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-bold ${Number(d.balance) >= 0 ? "text-primary-600" : "text-accent-500"}`}>
                      ${Number(d.balance).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-2 pt-2 border-t border-background-200/70" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => openDetail(d)} className="flex-1 py-2 bg-background-100 rounded-lg text-xs font-medium text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer flex items-center justify-center gap-1">
                    <i className="ri-eye-line"></i> Detay
                  </button>
                  <button onClick={() => openPayment(d)} className="flex-1 py-2 bg-accent-100 rounded-lg text-xs font-medium text-accent-700 hover:bg-accent-200/70 transition-colors cursor-pointer flex items-center justify-center gap-1">
                    <i className="ri-money-dollar-circle-line"></i> Ödeme Al
                  </button>
                  <button onClick={() => openEdit(d)} className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-400 hover:text-primary-600 cursor-pointer">
                    <i className="ri-edit-line"></i>
                  </button>
                  <button onClick={() => deleteDealer(d.id)} className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-400 hover:text-accent-500 cursor-pointer">
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950"><a id="dealer-form">{editing ? "Cari Bayi Düzenle" : "Yeni Cari Bayi"}</a></h4>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-3">
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Bayi Adı *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Telefon</label><input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Adres</label><input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1">Bakiye</label><input type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: Number(e.target.value) })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={save} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentDealer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => { if (!paymentSuccess) setShowPaymentModal(false); }}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            {!paymentSuccess ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-base font-semibold text-foreground-950"><a id="payment-form">Ödeme Al</a></h4>
                  <button onClick={() => setShowPaymentModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
                </div>
                <div className="bg-background-100 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">Bayi</span>
                    <span className="text-sm font-semibold text-foreground-950">{paymentDealer.name}</span>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Ödeme Tutarı *</label>
                  <div className="relative">
                    <input type="number" min="0.01" step="0.01" value={paymentAmount || ""} onChange={(e) => setPaymentAmount(Number(e.target.value))} placeholder="0,00" className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400">$</span>
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-xs font-medium text-foreground-500 mb-2">Ödeme Yöntemi</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ id: "nakit", label: "Nakit", icon: "ri-money-dollar-circle-line" }, { id: "pos", label: "POS", icon: "ri-bank-card-line" }, { id: "iban", label: "IBAN", icon: "ri-bank-line" }].map((method) => (
                      <button key={method.id} onClick={() => setPaymentMethod(method.id)} className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg text-xs font-medium transition-colors cursor-pointer ${paymentMethod === method.id ? "bg-accent-100 border border-accent-200 text-accent-700" : "bg-background-100 border border-background-200/70 text-foreground-500 hover:bg-background-200/70"}`}>
                        <i className={`${method.icon} text-base`}></i>{method.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowPaymentModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
                  <button onClick={confirmPayment} disabled={!paymentMethod || paymentAmount <= 0 || submitting} className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                    {submitting ? "Kaydediliyor..." : <><i className="ri-check-line mr-1.5"></i>Ödemeyi Onayla</>}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-14 h-14 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-accent-600"></i>
                </div>
                <h4 className="text-base font-semibold text-foreground-950 mb-1"><a id="payment-success">Ödeme Alındı!</a></h4>
                <p className="text-sm text-foreground-500 mb-6">{paymentDealer.name} adlı bayiden <strong className="text-foreground-900">${paymentAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong> tahsil edildi.</p>
                <button onClick={() => setShowPaymentModal(false)} className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">Kapat</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailDealer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDetailDealer(null)}>
          <div className="bg-background-50 rounded-2xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200/70 shrink-0">
              <div>
                <h4 className="text-base font-semibold text-foreground-950"><a id="dealer-detail">{detailDealer.name}</a></h4>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                  {detailDealer.phone && <span className="text-xs text-foreground-500 flex items-center gap-1"><i className="ri-phone-line text-xs"></i>{detailDealer.phone}</span>}
                  {detailDealer.address && <span className="text-xs text-foreground-500 flex items-center gap-1"><i className="ri-map-pin-line text-xs"></i>{detailDealer.address}</span>}
                </div>
              </div>
              <button onClick={() => setDetailDealer(null)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer shrink-0"><i className="ri-close-line"></i></button>
            </div>

            {/* Content - All panels visible at once */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
                </div>
              ) : (
                <>
                  {/* Bakiye Özet Kartı */}
                  <div className="bg-background-100 rounded-2xl border border-background-200/70 p-5">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                          <i className="ri-wallet-3-line text-xl text-primary-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 font-medium uppercase tracking-wide">Güncel Bakiye</p>
                          <p className={`text-2xl font-bold ${Number(detailDealer.balance) >= 0 ? "text-foreground-950" : "text-accent-500"}`}>
                            ${Number(detailDealer.balance).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="text-center">
                          <div className="flex items-center gap-1.5 text-foreground-500 text-xs mb-1">
                            <i className="ri-shopping-bag-line"></i> Toplam Satış
                          </div>
                          <p className="text-lg font-bold text-foreground-900">${totalSalesAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                          {salesDetail.length > 0 && <p className="text-[10px] text-foreground-400">{salesDetail.length} işlem</p>}
                        </div>
                        <div className="w-px h-10 bg-background-200/70"></div>
                        <div className="text-center">
                          <div className="flex items-center gap-1.5 text-foreground-500 text-xs mb-1">
                            <i className="ri-money-dollar-circle-line"></i> Toplam Tahsilat
                          </div>
                          <p className="text-lg font-bold text-emerald-600">${totalPaymentAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                          {paymentsDetail.length > 0 && <p className="text-[10px] text-foreground-400">{paymentsDetail.length} işlem</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Üçlü Kart Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Satış Geçmişi Kartı */}
                    <div className="bg-background-100 rounded-2xl border border-background-200/70 flex flex-col">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70 shrink-0">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
                            <i className="ri-shopping-bag-line text-primary-600 text-sm"></i>
                          </div>
                          <h4 className="text-sm font-semibold text-foreground-950">Satış Geçmişi</h4>
                        </div>
                        {salesDetail.length > 0 && (
                          <span className="text-xs text-foreground-400 bg-background-50 px-2 py-0.5 rounded-full">{salesDetail.length}</span>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto max-h-[380px]">
                        {salesDetail.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                            <div className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mb-3">
                              <i className="ri-shopping-bag-line text-foreground-300 text-lg"></i>
                            </div>
                            <p className="text-sm text-foreground-400">Henüz satış kaydı yok</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-background-200/70">
                            {salesDetail.map((sale) => (
                              <div key={sale.id} className="px-4 py-3">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${paymentMethodLabel(sale.payment_method).color}`}>
                                    <i className={`${paymentMethodLabel(sale.payment_method).icon} text-[10px]`}></i>
                                    {paymentMethodLabel(sale.payment_method).label}
                                  </span>
                                  <span className="text-sm font-bold text-foreground-950">${Number(sale.total_amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="space-y-1">
                                  {(sale.sale_items || []).map((item, idx) => {
                                    const product = productsMap[item.product_id];
                                    return (
                                      <div key={item.id || idx}>
                                        <div className="flex items-center justify-between text-xs">
                                          <span className="text-foreground-600 truncate max-w-[120px]">
                                            {product?.name || "Ürün"}
                                            {product?.brand && <span className="text-foreground-400 ml-1">({product.brand})</span>}
                                          </span>
                                          <span className="text-foreground-500 whitespace-nowrap">{item.quantity} x ${Number(item.unit_price).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                                        </div>
                                        {item.description && (
                                          <p className="text-[10px] text-foreground-400 mt-0.5 ml-0.5 italic line-clamp-2" title={item.description}>
                                            "{item.description}"
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                <p className="text-[10px] text-foreground-400 mt-1.5 flex items-center gap-1">
                                  <i className="ri-time-line text-[10px]"></i>
                                  {new Date(sale.created_at).toLocaleString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Ödeme Geçmişi Kartı */}
                    <div className="bg-background-100 rounded-2xl border border-background-200/70 flex flex-col">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70 shrink-0">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <i className="ri-money-dollar-circle-line text-emerald-600 text-sm"></i>
                          </div>
                          <h4 className="text-sm font-semibold text-foreground-950">Ödeme Geçmişi</h4>
                        </div>
                        {paymentsDetail.length > 0 && (
                          <span className="text-xs text-foreground-400 bg-background-50 px-2 py-0.5 rounded-full">{paymentsDetail.length}</span>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto max-h-[380px]">
                        {paymentsDetail.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                            <div className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mb-3">
                              <i className="ri-money-dollar-circle-line text-foreground-300 text-lg"></i>
                            </div>
                            <p className="text-sm text-foreground-400">Henüz ödeme kaydı yok</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-background-200/70">
                            {paymentsDetail.map((pay) => {
                              const status = paymentStatusLabel(pay.status);
                              return (
                                <div key={pay.id} className="px-4 py-3">
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-bold text-emerald-600">-${Number(pay.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                                      <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${status.color}`}>{status.label}</span>
                                    </div>
                                    {pay.receipt_url && (
                                      <a href={pay.receipt_url} target="_blank" rel="nofollow" className="text-[10px] text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                                        <i className="ri-file-image-line mr-0.5"></i>Dekont
                                      </a>
                                    )}
                                  </div>
                                  {pay.note && <p className="text-xs text-foreground-500 mb-0.5">{pay.note}</p>}
                                  <p className="text-[10px] text-foreground-400">{new Date(pay.created_at).toLocaleString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Ürünler Kartı */}
                    <div className="bg-background-100 rounded-2xl border border-background-200/70 flex flex-col">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70 shrink-0">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center">
                            <i className="ri-box-3-line text-secondary-600 text-sm"></i>
                          </div>
                          <h4 className="text-sm font-semibold text-foreground-950">Ürünler</h4>
                        </div>
                        <button
                          onClick={() => setShowProductAdd(true)}
                          className="flex items-center gap-1 px-2.5 py-1 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-add-line text-xs"></i> Ekle
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto max-h-[380px]">
                        <DealerProductsTab
                          currentDealerId={detailDealer.id}
                          dealerId={dealerId}
                          dealerName={detailDealer.name}
                          onDataChanged={fetchDealers}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Add Modal */}
      {showProductAdd && detailDealer && (
        <DealerProductAddModal
          dealerId={dealerId}
          currentDealerId={detailDealer.id}
          currentDealerName={detailDealer.name}
          onClose={() => setShowProductAdd(false)}
          onSuccess={() => {
            fetchDealers();
            setShowProductAdd(false);
          }}
        />
      )}
    </div>
  );
}